import { useCallback, useState, type ReactNode } from 'react';
import type { dashboard } from '@cwos/api-client';
import { makeLinePath, niceScale, xAt, type AxisScale } from './chartMath.js';

/**
 * Hand-rolled SVG charts for the analytics workspace — zero chart-library
 * dependencies, consistent with the dep-free admin UI. Colors come from the
 * design-token CSS variables (never raw hex in component code).
 */

const PAD = { left: 44, right: 12, top: 12, bottom: 26 };

/**
 * The width a chart is drawn at before it has measured itself: server render,
 * jsdom, and the first paint. Also the floor — a plot narrower than this stops
 * being readable long before the axis stops fitting.
 */
const FALLBACK_WIDTH = 640;

/**
 * The rendered width of an element, in CSS pixels.
 *
 * These charts are SVG with `width: 100%`, and an SVG with a fixed `viewBox`
 * scales EVERYTHING inside it — including type. At 640 units in a 1080px panel
 * a 10px axis label renders at about 17px, and in a 380px column it renders at
 * about 6px, so the same chart had axis labels that were oversized on a
 * full-width panel and unreadable in a three-across grid. Nothing inside SVG
 * fixes that: `vector-effect` covers strokes, not glyphs.
 *
 * So the chart is drawn in real pixels instead. The viewBox matches the
 * measured width one-to-one, which means 10px is 10px at every panel size, a
 * 1px gridline is one device-independent pixel, and the plot gets wider rather
 * than more magnified as the panel grows.
 */
function useMeasuredWidth(): [number, (el: HTMLDivElement | null) => (() => void) | undefined] {
  const [width, setWidth] = useState(FALLBACK_WIDTH);
  /**
   * A CALLBACK REF, not an effect on a ref object. The effect ran once, on
   * mount — and when that first render took an early return (`NoPoints`,
   * `AllHidden`) there was no element yet, so a chart whose data arrived a
   * render later stayed at the 640px fallback for the life of the page. A
   * callback ref runs whenever the element itself attaches.
   */
  const ref = useCallback((el: HTMLDivElement | null) => {
    // No ResizeObserver in jsdom. The fallback is a correct chart, just not a
    // measured one.
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width ?? 0;
      if (measured > 0) setWidth(Math.max(FALLBACK_WIDTH / 2, Math.round(measured)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [width, ref];
}

/**
 * The axis for a set of measured values: whole-number ticks when every value is
 * a whole number (a count), fractional steps only when the data needs them.
 */
export function scaleOf(values: readonly number[]): AxisScale {
  return niceScale(Math.max(0, ...values), values.every(Number.isInteger));
}

/**
 * ONE POINT OF A SERIES, AND WHY IT IS NOT A `number`.
 *
 * A `number[]` has exactly one spelling for "there is no figure here" — `0` —
 * and a zero on a trend line is an affirmative statement that the organization
 * did nothing that week. `/analytics/trends` now suppresses individual weekly
 * points when fewer than the published floor of people stand behind them
 * (`kind: 'suppressed_small_cohort'` on the wire), so drawing one as `0` would
 * put a DOWNWARD SPIKE on the chart: "activity collapsed" said confidently
 * about a week we merely declined to describe. That is worse than the
 * over-disclosure it replaced, because it is a false statement rather than an
 * excessive true one.
 *
 * `absence` IS REQUIRED WHEN `value` IS NULL, exactly as {@link GroupedPoint}
 * requires it and for the same reason: a blank the chart cannot explain is
 * unrepresentable. The sentence comes from the server (`message` on the
 * suppressed arm) so the chart never invents a second one.
 */
export interface SeriesPoint {
  value: number | null;
  /** What to say when `value` is null. Never empty. */
  absence: string;
}

/**
 * Wrap plain measurements for a chart that cannot carry a suppression.
 *
 * FOR SERIES WHOSE SOURCE HAS NO SUPPRESSED ARM — `/analytics/care-gaps/trend`,
 * `/analytics/documentation/trend`, and the saved-widget `SeriesData`. Those
 * payloads are `number[]` on the wire, so every point they have IS a
 * measurement and there is no absence to describe.
 *
 * It is a named function rather than an inline `.map` so that a future series
 * that DOES gain a suppressed arm is a grep away, and so nobody spells the
 * conversion two ways.
 */
export function measuredPoints(values: readonly number[]): SeriesPoint[] {
  return values.map((value) => ({ value, absence: '' }));
}

export interface Series {
  name: string;
  /**
   * A CSS `var()` string. Use a `--chart-*` tone for a plain measure; a status
   * tone is only correct when the series IS a severity (overdue, gapped).
   */
  color: string;
  /**
   * `points`, NOT `values`. The rename is deliberate and is the whole safety
   * of this change: a caller that still hands over `number[]` fails to
   * compile rather than silently losing a suppression.
   */
  points: SeriesPoint[];
  /**
   * Draw this series dashed.
   *
   * For the baseline in a plan-versus-actual pair. Two lines told apart only by
   * hue are two lines a reader with a colour vision deficiency cannot tell
   * apart at all, and on these charts the pair is always the same shape:
   * "scheduled" under "completed", "with plan" under "gapped". A dash pattern
   * separates them without colour and reads as provisional, which is what a
   * plan is.
   */
  dashed?: boolean;
}

interface BaseChartProps {
  labels: string[];
  height?: number;
  formatValue?: (v: number) => string;
}

/**
 * Which x-axis labels to draw.
 *
 * Every Nth label, plus the final one so the axis says where the range ends.
 * That pairing is what used to collide: with 26 weekly points the regular ticks
 * land on 0, 4, ... 24 and the endpoint is 25, so two labels rendered one index
 * apart and overprinted each other ("Aug 1Aug 1" on the six-month charts).
 *
 * The endpoint wins. When the preceding regular tick is closer than half the
 * normal spacing, that tick is dropped rather than the endpoint, because "where
 * does this series end" is the more useful of the two and dropping the endpoint
 * would leave the right edge unlabelled.
 */
export function xTickIndices(labelCount: number): number[] {
  if (labelCount <= 0) return [];
  if (labelCount === 1) return [0];
  const every = Math.max(1, Math.ceil(labelCount / 8));
  const last = labelCount - 1;
  const out: number[] = [];
  for (let i = 0; i <= last; i += every) out.push(i);
  if (out[out.length - 1] !== last) {
    if (last - out[out.length - 1]! < every / 2) out.pop();
    out.push(last);
  }
  return out;
}

/**
 * A shared SVG scaffold: plot area, y-axis gridlines and labels, x labels.
 *
 * `xOf` places the x labels where the MARKS are. Labels used to sit at line
 * (endpoint) positions while bars sit at slot centres, so on a bar chart the
 * first and last labels were half a slot off their bars.
 */
function ChartFrame({
  labels,
  height,
  scale,
  formatValue,
  label,
  width,
  xOf,
  children,
}: BaseChartProps & {
  scale: AxisScale;
  label: string;
  width: number;
  xOf?: (index: number) => number;
  children: ReactNode;
}) {
  const h = height ?? 220;
  const { max, ticks } = scale;
  const innerH = h - PAD.top - PAD.bottom;
  const shown = new Set(xTickIndices(labels.length));
  const xLabel = xOf ?? ((i: number) => xAt(i, labels.length, width, PAD));

  return (
    <svg
      viewBox={`0 0 ${width} ${h}`}
      width={width}
      height={h}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      role="img"
      aria-label={label}
    >
      {ticks.map((t) => {
        const y = PAD.top + innerH - (t / (max > 0 ? max : 1)) * innerH;
        return (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={y}
              y2={y}
              style={{ stroke: 'var(--color-line)' }}
              strokeWidth={1}
              shapeRendering="crispEdges"
            />
            <text
              x={PAD.left - 6}
              y={y + 3.5}
              textAnchor="end"
              fontSize={10}
              style={{ fill: 'var(--color-soft)', fontFamily: 'var(--font-mono)' }}
            >
              {formatValue ? formatValue(t) : t}
            </text>
          </g>
        );
      })}
      {labels.map((label, i) => {
        if (!shown.has(i)) return null;
        return (
          <text
            key={`${label}:${i}`}
            x={xLabel(i)}
            y={h - 8}
            textAnchor="middle"
            fontSize={10}
            style={{ fill: 'var(--color-soft)', fontFamily: 'var(--font-mono)' }}
          >
            {label}
          </text>
        );
      })}
      {children}
    </svg>
  );
}

/**
 * F274 — THE CHART'S VALUES, FOR SOMEONE WHO CANNOT SEE THE CHART.
 *
 * The frame carries `role="img"`, which makes the whole `<svg>` a LEAF in the
 * accessibility tree: every label on every child is unreachable, no matter how
 * carefully it was written. So the per-bar `aria-label`s below, and the
 * per-point ones on the line, were never announced to anyone — and until this
 * component existed the entire analytics surface said one thing to a screen
 * reader, the same generic sentence on every chart, with no values at all.
 *
 * Point markers cannot be the fix. They are drawn only when a drilldown
 * handler is supplied, and rendering them unconditionally would put a marker on every point
 * of every chart, which on a 26-week daily series is a smear rather than a
 * chart. Accessibility must not be downstream of a visual decision or of
 * whether some caller wanted a click handler.
 *
 * This is the standard alternative instead: the same numbers as a table,
 * visually hidden, positioned after the picture. It costs nothing on screen,
 * it scales to dense series because a table is navigable, and it stays correct
 * when the drilldown is eventually wired.
 */
function ChartValues({
  label,
  labels,
  series,
  formatValue,
}: {
  label: string;
  labels: string[];
  series: Series[];
  formatValue?: (v: number) => string;
}) {
  /**
   * THREE ANSWERS, NOT TWO — and the third is the one this table was added for.
   *
   *   off the end of the series → "no value recorded" (nobody measured it)
   *   `value: null`             → the point's own `absence` sentence, verbatim
   *                               from the server, which says the figure is
   *                               hidden and is NOT a zero
   *   a number                  → the figure
   *
   * A screen-reader user is the audience with no way to see the dashed gap on
   * the picture, so collapsing the middle case into either neighbour takes the
   * whole disclosure away from exactly the reader who cannot infer it.
   */
  const show = (p: SeriesPoint | undefined) => {
    if (p === undefined) return 'no value recorded';
    if (p.value === null) return p.absence;
    return formatValue ? formatValue(p.value) : String(p.value);
  };
  return (
    <table className="rn-sr-only">
      <caption>{`${label} — the same data as a table`}</caption>
      <thead>
        <tr>
          <th scope="col">Point</th>
          {series.map((s) => (
            <th key={s.name} scope="col">
              {s.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {labels.map((l, i) => (
          <tr key={l ?? i}>
            <th scope="row">{l}</th>
            {series.map((s) => (
              <td key={s.name}>{show(s.points[i])}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export interface LineChartProps extends BaseChartProps {
  series: Series[];
  /** Accessible name. Defaults to the series it plots. */
  label?: string;
  /**
   * How much of each week a point covers, aligned to `labels`
   * (`AnalyticsTrends.coverage`). A `to_date` or `partial` point is drawn
   * DASHED and hollow and named under the chart: the in-progress week used to
   * be drawn, and dotted, as if it were a complete one — which reads as a
   * collapse in the most recent week.
   */
  coverage?: readonly dashboard.WeekCoverage[];
  onPointClick?: (seriesIndex: number, pointIndex: number) => void;
}

/** What a reader is told a short point covers, in the table and under the chart. */
const COVERAGE_WORDS: Record<Exclude<dashboard.WeekCoverage, 'full'>, string> = {
  to_date: 'week to date',
  partial: 'part of a week',
};

export function LineChart({
  series,
  labels,
  height,
  formatValue,
  label,
  coverage,
  onPointClick,
}: LineChartProps) {
  const [width, box] = useMeasuredWidth();
  // Every chart used to announce the same sentence. Name it from what it
  // actually plots, so a screen reader distinguishes two charts on one screen.
  const name = label ?? series.map((s) => s.name).join(', ') ?? 'Trend';
  const h = height ?? 220;
  const allPoints = series.flatMap((s) => s.points);
  const measured = allPoints.flatMap((p) => (p.value === null ? [] : [p.value]));
  const scale = scaleOf(measured);
  const short = (i: number): boolean => (coverage?.[i] ?? 'full') !== 'full';

  // An empty series drew the whole frame — axis, gridlines, tick labels at
  // 0/0.25/0.5/0.75/1 from `niceMax(0) === 1` — and no line. That is a
  // complete-looking picture of a period in which nothing happened, and it was
  // rendered identically for a period nobody had measured. A chart with no
  // points is not a flat chart.
  if (allPoints.length === 0 || labels.length === 0) {
    return <NoPoints height={h} />;
  }

  // EVERY POINT HIDDEN IS NOT "NO POINTS", and it must not borrow that
  // sentence. `NoPoints` says the chart has nothing to draw, which a reader
  // hears as "nothing happened" or "this is broken"; here there IS data and it
  // is being withheld for a stated reason. Drawing the frame instead would put
  // a complete-looking 0/0.25/0.5/0.75/1 axis over figures nobody was given.
  if (measured.length === 0) {
    return <AllHidden height={h} reason={allPoints.find((p) => p.absence)?.absence ?? ''} />;
  }

  const rowLabels = labels.map((l, i) => {
    const c = coverage?.[i];
    return c && c !== 'full' ? `${l} (${COVERAGE_WORDS[c]})` : l;
  });

  return (
    <div ref={box}>
      {series.length > 1 ? <Legend series={series} /> : null}
      <ChartFrame
        labels={labels}
        height={h}
        scale={scale}
        formatValue={formatValue}
        label={name}
        width={width}
      >
        {/* THE MARKED GAP, DRAWN ONCE FOR THE CHART RATHER THAN ONCE PER
            SERIES. A slot left completely empty reads as zero to somebody
            looking at it, which is the same lie as drawing the point; the
            dashed rule says "there is something here that is not being shown".
            One rule per hidden bucket, because the server suppresses a whole
            block together and two overlapping rules would just be darker. */}
        {hiddenIndices(series).map((i) => (
          <line
            key={`gap-${i}`}
            x1={xAt(i, labels.length, width, PAD)}
            x2={xAt(i, labels.length, width, PAD)}
            y1={PAD.top}
            y2={h - PAD.bottom}
            style={{ stroke: 'var(--chart-context)' }}
            strokeWidth={1}
            strokeDasharray="3 3"
            aria-hidden="true"
          />
        ))}
        {series.map((s, si) => {
          const values = s.points.map((p) => p.value);
          const { points } = makeLinePath(values, width, h, scale.max, PAD);
          // The full weeks are the line; a short week joins it with a dashed
          // link, so the shape never claims the week to date is a whole week.
          const { segments, dots } = makeLinePath(
            values.map((v, i) => (short(i) ? null : v)),
            width,
            h,
            scale.max,
            PAD,
          );
          const shortLinks = points.flatMap((p, i) => {
            const prev = i > 0 ? points[i - 1] : null;
            if (!p || !prev || !(short(i) || short(i - 1))) return [];
            return [
              `M${prev.x.toFixed(1)},${prev.y.toFixed(1)} L${p.x.toFixed(1)},${p.y.toFixed(1)}`,
            ];
          });
          // The LAST MEASURED point, marked — not `points[points.length - 1]`,
          // which is `null` when the newest week is the hidden one. Putting the
          // "you are here" dot on a suppressed bucket would draw the reading we
          // just refused to publish.
          const lastIndex = points.reduce((at, p, i) => (p === null ? at : i), -1);
          const lastMeasured = lastIndex >= 0 ? points[lastIndex]! : null;
          return (
            <g key={s.name}>
              {segments.map((d) => (
                <path
                  key={d}
                  d={d}
                  fill="none"
                  strokeWidth={s.dashed ? 1.75 : 2.25}
                  strokeDasharray={s.dashed ? '5 4' : undefined}
                  style={{ stroke: s.color }}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ))}
              {shortLinks.map((d) => (
                <path
                  key={`short-${d}`}
                  d={d}
                  fill="none"
                  strokeWidth={1.5}
                  strokeDasharray="2 4"
                  style={{ stroke: s.color }}
                  strokeLinecap="round"
                />
              ))}
              {/* A measured point with hidden neighbours on both sides is a run
                  of one and strokes nothing. Without this it would vanish, and
                  a published figure disappearing is its own small lie. */}
              {dots.map((d) => (
                <circle key={`${d.x}:${d.y}`} cx={d.x} cy={d.y} r={2} style={{ fill: s.color }} />
              ))}
              {/* The last point, marked. A trend chart is read right-to-left —
                  "where are we now, and how did we get here" — and the current
                  value was the one thing on it with no emphasis at all. Only on
                  a measured series: a plan does not have a latest reading.
                  HOLLOW when it is the week to date: not a finished reading. */}
              {!s.dashed && lastMeasured !== null ? (
                <circle
                  cx={lastMeasured.x}
                  cy={lastMeasured.y}
                  r={3}
                  strokeWidth={1.5}
                  style={
                    short(lastIndex)
                      ? { fill: 'var(--color-surface)', stroke: s.color }
                      : { fill: s.color }
                  }
                />
              ) : null}
              {onPointClick
                ? points.map((p, pi) =>
                    p === null ? null : (
                      <g
                        key={pi}
                        cursor="pointer"
                        role="button"
                        aria-label={pointLabel(s.name, s.points[pi], formatValue)}
                        onClick={() => onPointClick(si, pi)}
                      >
                        {/* A 10px target around a 3.5px mark — the mark alone
                            is too small to hit with a finger. */}
                        <circle cx={p.x} cy={p.y} r={10} style={{ fill: 'transparent' }} />
                        <circle
                          cx={p.x}
                          cy={p.y}
                          r={3.5}
                          style={{ fill: 'var(--color-surface)', stroke: s.color }}
                          strokeWidth={1.5}
                        />
                      </g>
                    ),
                  )
                : null}
            </g>
          );
        })}
      </ChartFrame>
      <CoverageNote labels={labels} coverage={coverage} />
      <ChartValues label={name} labels={rowLabels} series={series} formatValue={formatValue} />
    </div>
  );
}

/** Which series is which, for a chart with more than one. Swatches mirror the strokes. */
function Legend({ series }: { series: readonly Series[] }) {
  return (
    <ul
      aria-hidden="true"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--space-12)',
        listStyle: 'none',
        margin: '0 0 var(--space-6)',
        padding: 0,
        fontSize: 12,
        color: 'var(--color-ink2)',
      }}
    >
      {series.map((s) => (
        <li key={s.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width={18} height={8} aria-hidden="true">
            <line
              x1={1}
              x2={17}
              y1={4}
              y2={4}
              strokeWidth={2.25}
              strokeDasharray={s.dashed ? '5 4' : undefined}
              style={{ stroke: s.color }}
            />
          </svg>
          {s.name}
        </li>
      ))}
    </ul>
  );
}

/** Names the short weeks under the chart, so the dashed tail is never a riddle. */
function CoverageNote({
  labels,
  coverage,
}: {
  labels: readonly string[];
  coverage: readonly dashboard.WeekCoverage[] | undefined;
}) {
  const of = (kind: 'to_date' | 'partial') =>
    labels.filter((_, i) => coverage?.[i] === kind).join(', ');
  const toDate = of('to_date');
  const partial = of('partial');
  if (!toDate && !partial) return null;
  return (
    <p className="rn-muted" style={{ fontSize: 12, margin: 'var(--space-4) 0 0' }}>
      {toDate ? `${toDate} is the week to date, not a full week. ` : ''}
      {partial
        ? `${partial}: part of a week only — the period or the records start part-way through it.`
        : ''}
    </p>
  );
}

export interface BarChartProps extends BaseChartProps {
  values: number[];
  color?: string;
  /** Accessible name for the chart and its data table. */
  label?: string;
  onBarClick?: (index: number) => void;
}

export function BarChart({
  values,
  labels,
  height,
  formatValue,
  color,
  label,
  onBarClick,
}: BarChartProps) {
  const [width, box] = useMeasuredWidth();
  const name = label ?? 'Chart';
  const h = height ?? 220;
  if (values.length === 0 || labels.length === 0) {
    return <NoPoints height={h} />;
  }
  const scale = scaleOf(values);
  const max = scale.max;
  const barColor = color ?? 'var(--chart-1)';
  const innerW = width - PAD.left - PAD.right;
  const innerH = h - PAD.top - PAD.bottom;
  const n = values.length;
  const slot = n > 0 ? innerW / n : innerW;
  const barW = Math.min(28, Math.max(4, slot * 0.6));
  const centreOf = (i: number): number => PAD.left + slot * i + slot / 2;

  return (
    <div ref={box}>
      <ChartFrame
        labels={labels}
        height={h}
        scale={scale}
        formatValue={formatValue}
        label={name}
        width={width}
        xOf={centreOf}
      >
        {values.map((v, i) => {
          const x = centreOf(i) - barW / 2;
          const bh = (Math.max(0, v) / (max > 0 ? max : 1)) * innerH;
          const y = PAD.top + innerH - bh;
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={barW}
              height={bh}
              rx={3}
              style={{ fill: barColor }}
              cursor={onBarClick ? 'pointer' : undefined}
              role={onBarClick ? 'button' : undefined}
              aria-label={`${labels[i] ?? i}: ${formatValue ? formatValue(v) : v}`}
              onClick={onBarClick ? () => onBarClick(i) : undefined}
            />
          );
        })}
      </ChartFrame>
      <ChartValues
        label={name}
        labels={labels}
        series={[{ name, color: barColor, points: measuredPoints(values) }]}
        formatValue={formatValue}
      />
    </div>
  );
}

/**
 * The chart's own "there is nothing here" — and deliberately not an axis.
 *
 * It says nothing about WHY there are no points, because a chart cannot know:
 * a scoped read, a failed one and a genuinely quiet period all arrive as an
 * empty array. Saying "no data for this period" would pick one of the three.
 * The surrounding panel owns that sentence; this only refuses to draw a picture
 * of data it does not have.
 */
function NoPoints({ height }: { height: number }) {
  return (
    <div
      role="status"
      style={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--color-ink2)',
        fontSize: 13,
      }}
    >
      No points to plot.
    </div>
  );
}

/**
 * A point that is not in the series has no value, and `?? 0` announced it to a
 * screen-reader user as zero — the one audience with no way to see that the dot
 * they are on has nothing under it. A point that is HIDDEN gets the server's
 * own sentence, which says so and says it is not a zero.
 */
function pointLabel(
  name: string,
  point: SeriesPoint | undefined,
  formatValue?: (v: number) => string,
): string {
  if (point === undefined) return `${name}: no value recorded`;
  if (point.value === null) return `${name}: ${point.absence}`;
  return `${name}: ${formatValue ? formatValue(point.value) : String(point.value)}`;
}

/** Every bucket index at which ANY series is hidden. */
function hiddenIndices(series: readonly Series[]): number[] {
  const out = new Set<number>();
  for (const s of series) {
    s.points.forEach((p, i) => {
      if (p.value === null) out.add(i);
    });
  }
  return [...out].sort((a, b) => a - b);
}

/**
 * EVERY POINT HIDDEN — and deliberately NOT `NoPoints`.
 *
 * `NoPoints` says the chart has nothing to draw, which is the ABSENT sentence.
 * This one says a real series exists and is not being shown, in the server's
 * own words. Collapsing the two would tell a reader the organization recorded
 * nothing when in fact every week of it stood on too few people to publish.
 */
function AllHidden({ height, reason }: { height: number; reason: string }) {
  return (
    <div
      role="status"
      style={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '0 16px',
        color: 'var(--color-ink2)',
        fontSize: 13,
      }}
    >
      {reason === '' ? 'Every point on this chart is hidden. It is not zero.' : reason}
    </div>
  );
}

// ---------------------------------------------------------------------------
// F307 — a chart whose axis IS the breakdown
// ---------------------------------------------------------------------------

/**
 * One group of a saved breakdown, as this chart needs it.
 *
 * `absence` IS REQUIRED, and that is the whole reason this is not
 * {@link Series}. A `Series` carries `number[]`, so a group with no figure has
 * only one spelling available — `0` — and a bar of height zero is a claim that
 * the program did none of the work. But "no figure" is not one fact either: a
 * group can be blank because nothing was recorded, or because a real figure is
 * WITHHELD from this reader for being computed over too few people. Those are
 * different sentences, only the server knows which applies, and a chart that
 * accepted a bare `null` would have no way to say either. Requiring the
 * sentence makes an unexplained blank unrepresentable.
 */
export interface GroupedPoint {
  /** The group's own identifier — a program/site id, or a bucket start. */
  key: string;
  /** Its display name. This is what the axis and the data table say. */
  label: string;
  value: number | null;
  /** What to say when `value` is null. Never empty. */
  absence: string;
}

export interface GroupedChartProps {
  points: GroupedPoint[];
  /** `bar` for a category axis; `line` only where the axis is time. */
  chart: 'line' | 'bar';
  /** Accessible name for the chart and its data table. */
  label: string;
  /**
   * What the groups ARE — "Program", "Site", "Week". It becomes the data
   * table's first column header, so a reader hears "Program: Outpatient"
   * rather than an unlabelled row header.
   */
  groupHeader: string;
  height?: number;
  color?: string;
  formatValue?: (v: number) => string;
}

/**
 * A BREAKDOWN, DRAWN — one mark per group, and every group reachable.
 *
 * WHY IT IS NOT `BarChart` WITH DIFFERENT NUMBERS. `BarChart` takes
 * `values: number[]` beside `labels: string[]`: two arrays joined by index, no
 * identity on either side, and no way to carry a group that has no figure. This
 * chart's axis is a set of NAMED things, some of which may be blank for a reason
 * that has to travel with them.
 *
 * THE HIDDEN TABLE IS THE DELIVERABLE, not a courtesy. The `<svg>` carries
 * `role="img"` and is therefore a LEAF in the accessibility tree — every label
 * inside it is unreachable however carefully it is written (F274). For an
 * ordinary trend that costs a reader the values; for a BREAKDOWN it costs them
 * the grouping itself, which is the entire thing the widget is for. So the table
 * carries one row per group, headed by the group's name, and a blank cell says
 * WHY it is blank in words rather than being empty.
 *
 * A BLANK GROUP IS DRAWN AS A GAP, NOT AS A ZERO, and the gap is MARKED: a
 * dashed rule stands in the group's slot where its bar would be. A slot left
 * completely empty reads as "zero" to someone looking at it, which is the same
 * lie as drawing the bar — the mark says "there is something here that is not
 * being shown", and the note the widget renders above the chart says how many
 * and why.
 */
export function GroupedChart({
  points,
  chart,
  label,
  groupHeader,
  height,
  color,
  formatValue,
}: GroupedChartProps) {
  // Hooks before the early returns — a breakdown that arrives empty on one
  // render and populated on the next must not change the hook count.
  const [width, box] = useMeasuredWidth();
  const h = height ?? 220;
  // A breakdown with no groups at all is not an empty chart, it is no chart.
  // Drawing the frame would render an axis over a set of nothing.
  if (points.length === 0) return <NoPoints height={h} />;

  const labels = points.map((p) => p.label);
  const measured = points.flatMap((p) => (p.value === null ? [] : [p.value]));
  // EVERY group blank is still not a chart. Reaching `niceMax(0)` would draw a
  // complete-looking 0/0.25/0.5/0.75/1 axis over data nobody was given.
  if (measured.length === 0) return <NoPoints height={h} />;

  const scale = scaleOf(measured);
  const max = scale.max;
  // The categorical ramp, not --color-sky. A brand tone on a data mark teaches
  // a reader that the number carries a brand meaning; --chart-1 is the ramp's
  // first tone and is what every other chart in this file now defaults to.
  const markColor = color ?? 'var(--chart-1)';
  const innerW = width - PAD.left - PAD.right;
  const innerH = h - PAD.top - PAD.bottom;
  const n = points.length;
  const slot = innerW / n;
  const barW = Math.min(28, Math.max(4, slot * 0.6));
  const safeMax = max > 0 ? max : 1;

  const centreOf = (i: number): number =>
    chart === 'line'
      ? n <= 1
        ? PAD.left + innerW / 2
        : PAD.left + (i / (n - 1)) * innerW
      : PAD.left + slot * i + slot / 2;
  const yOf = (v: number): number => PAD.top + innerH - (Math.max(0, v) / safeMax) * innerH;

  /**
   * A LINE IS DRAWN IN SEGMENTS, BROKEN AT EVERY BLANK GROUP.
   *
   * Joining across a gap would draw a slope through a bucket nobody measured —
   * an interpolation asserted over exactly the days the reader was told nothing
   * about, which is the same family of claim a `line` over a CATEGORY axis makes
   * and which the server already refuses. So a run of measured points is one
   * path, and the gap between two runs stays empty.
   */
  const segments: string[] = [];
  if (chart === 'line') {
    let run: string[] = [];
    points.forEach((p, i) => {
      if (p.value === null) {
        if (run.length > 1) segments.push(run.join(' '));
        run = [];
        return;
      }
      const cmd = run.length === 0 ? 'M' : 'L';
      run.push(`${cmd}${centreOf(i).toFixed(1)},${yOf(p.value).toFixed(1)}`);
    });
    if (run.length > 1) segments.push(run.join(' '));
  }

  return (
    <div ref={box}>
      <ChartFrame
        labels={labels}
        height={h}
        scale={scale}
        formatValue={formatValue}
        label={label}
        width={width}
        xOf={centreOf}
      >
        {points.map((p, i) => {
          if (p.value === null) {
            // The MARKED gap. Muted and dashed so it reads as "withheld or
            // unrecorded", never as a measurement of zero.
            const x = centreOf(i);
            return (
              <line
                key={p.key}
                x1={x}
                x2={x}
                y1={PAD.top}
                y2={PAD.top + innerH}
                style={{ stroke: 'var(--chart-context)' }}
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            );
          }
          if (chart === 'line') {
            return (
              <circle
                key={p.key}
                cx={centreOf(i)}
                cy={yOf(p.value)}
                r={2.5}
                style={{ fill: markColor }}
              />
            );
          }
          const bh = (Math.max(0, p.value) / safeMax) * innerH;
          return (
            <rect
              key={p.key}
              x={centreOf(i) - barW / 2}
              y={PAD.top + innerH - bh}
              width={barW}
              height={bh}
              rx={3}
              style={{ fill: markColor }}
            />
          );
        })}
        {segments.map((d) => (
          <path
            key={d}
            d={d}
            fill="none"
            strokeWidth={2}
            style={{ stroke: markColor }}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
      </ChartFrame>
      <GroupedValues
        label={label}
        groupHeader={groupHeader}
        points={points}
        formatValue={formatValue}
      />
    </div>
  );
}

/**
 * The breakdown as a table, visually hidden — F274's answer, carrying the thing
 * a breakdown is actually for.
 *
 * ONE ROW PER GROUP, HEADED BY THE GROUP'S NAME. `ChartValues` above heads its
 * rows with a positional x label because that is all a `Series` has; here the
 * row header is the program or the week itself, and the column header says what
 * kind of thing that is — so a reader hears "Program / Outpatient / 41.5 hours"
 * rather than three unlabelled cells.
 *
 * AND A BLANK CELL SAYS WHY IT IS BLANK. `ChartValues` renders the one sentence
 * it can know ("no value recorded"); a breakdown cell has two possible sentences
 * and only the server knows which applies, so the point carries it. That is the
 * difference between a reader being told a program did nothing and being told a
 * real figure is withheld from them.
 */
function GroupedValues({
  label,
  groupHeader,
  points,
  formatValue,
}: {
  label: string;
  groupHeader: string;
  points: GroupedPoint[];
  formatValue?: (v: number) => string;
}) {
  const caption = `${label} — the same data as a table, one row per ${groupHeader.toLowerCase()}`;
  const show = (p: GroupedPoint): string => {
    if (p.value === null) return p.absence;
    return formatValue ? formatValue(p.value) : String(p.value);
  };
  return (
    <table className="rn-sr-only">
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">{groupHeader}</th>
          <th scope="col">{label}</th>
        </tr>
      </thead>
      <tbody>
        {points.map((p) => (
          <tr key={p.key}>
            <th scope="row">{p.label}</th>
            <td>{show(p)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
